/**
 * PartnerDeliveryEarnings — the 3PL manager's money view.
 * ─────────────────────────────────────────────────────────────────────────────
 * A delivery partner needs to run a fleet, so this answers the questions a fleet
 * manager actually asks: how many trips did we do, what did the deliveries earn,
 * what went to the riders and what is the partner's own share, which riders are
 * carrying the work, and how long a delivery takes us.
 *
 * What it deliberately does NOT show: the commission Lusuku earns on the goods,
 * the payment rail cost, or Lusuku's net take. That is not the partner's
 * business, and the server does not send it — /api/money/partner/:id returns
 * delivery figures only, and refuses a partner asking about another partner.
 *
 * Renders for a 3PL login reading itself, or for Lusuku staff inspecting a
 * partner (pass `asStaff`).
 */

import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { apiFetchPartnerMoneyAnalytics } from "../../api/index";
import {
  C,
  S,
  ErrorNote,
  Kpi,
  KpiRow,
  Loading,
  MoneyRow,
  SectionTitle,
  ShareList,
  VEHICLE_LABEL,
  WindowPicker,
  dayLabel,
  fmtMinutes,
  fmtPct,
  fmtUGX,
  moneyBarOptions,
  useMoneyReport,
} from "./moneyUi";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PAYOUT_LABEL = { SALARIED: "Salaried", PER_DELIVERY: "Per delivery" };

export default function PartnerDeliveryEarnings({ partnerId, asStaff = false, title }) {
  const { data, loading, error, days, setDays, refreshedAt } = useMoneyReport(
    (d) => apiFetchPartnerMoneyAnalytics(partnerId, { days: d }, { asStaff }),
    [partnerId, asStaff],
    { days: 30 }
  );

  const totals = data?.totals;

  const trendChart = useMemo(() => {
    const t = data?.trend || [];
    const base = moneyBarOptions({ stacked: true });
    return {
      data: {
        labels: t.map((d) => dayLabel(d.day)),
        datasets: [
          {
            label: "Rider earnings",
            data: t.map((d) => d.riderEarnings),
            backgroundColor: C.teal,
            stack: "fee",
            maxBarThickness: 26,
          },
          {
            label: "Partner share",
            data: t.map((d) => d.partnerEarnings),
            backgroundColor: C.gold,
            stack: "fee",
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const row = t[items[0]?.dataIndex];
                if (!row) return "";
                return `${row.delivered} delivered of ${row.orders} assigned`;
              },
              label: (ctx) => `${ctx.dataset.label}: ${fmtUGX(ctx.parsed.y)}`,
            },
          },
        },
      },
    };
  }, [data]);

  const vehicleRows = (data?.byVehicle || []).map((v) => ({
    key: v.loadClass,
    label: VEHICLE_LABEL[v.loadClass] || v.loadClass,
    amount: v.partnerEarnings,
    sub: `${v.deliveries} trip${v.deliveries === 1 ? "" : "s"} · riders ${fmtUGX(v.riderEarnings)}`,
    color: v.loadClass === "MOTORCYCLE" ? C.teal : C.navy,
  }));

  if (!partnerId) {
    return (
      <div style={S.wrap}>
        <ErrorNote>No partner account is attached to this login yet.</ErrorNote>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h4 style={S.title}>
            <i className="fas fa-truck-moving" style={{ color: C.teal }} />
            {title || "Delivery earnings"}
          </h4>
          <div style={S.subtitle}>
            Completed deliveries: what the fleet moved and what it earned
            {refreshedAt ? ` · updated ${refreshedAt.toLocaleTimeString()}` : ""}
          </div>
        </div>
        <WindowPicker value={days} onChange={setDays} />
      </div>

      {loading && <Loading what="delivery earnings" />}
      {!loading && error && <ErrorNote>{error}</ErrorNote>}

      {!loading && !error && totals && (
        <>
          <KpiRow>
            <Kpi
              label="Deliveries completed"
              value={String(totals.delivered)}
              meta={`${totals.orders} assigned · ${totals.open ?? 0} in progress · ${totals.cancelled} cancelled`}
              accent={C.teal}
            />
            <Kpi
              label="Partner share earned"
              value={fmtUGX(totals.partnerEarnings)}
              meta={`${fmtUGX(totals.averagePartnerEarningsPerDelivery)} per delivery`}
              accent={C.gold}
            />
            <Kpi
              label="Paid to riders"
              value={fmtUGX(totals.riderEarnings)}
              meta={
                totals.deliveryFee + (totals.deliveryFundedByLusuku || 0)
                  ? `${fmtPct((totals.riderEarnings / (totals.deliveryFee + (totals.deliveryFundedByLusuku || 0))) * 100, 0)} of delivery fees`
                  : null
              }
              accent={C.navy}
            />
            <Kpi
              label="Typical delivery time"
              value={fmtMinutes(data.medianDeliveryMinutes)}
              meta="Median, order placed to delivered"
              accent={C.green}
            />
          </KpiRow>

          <SectionTitle first>Earnings, day by day</SectionTitle>
          <div style={S.chartBox}>
            <Bar data={trendChart.data} options={trendChart.options} />
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <SectionTitle first>The delivery fee, split</SectionTitle>
              {/* A customer delivery offer: Lusuku paid part of the fee, and
                  the rider and your share were paid in full on the whole of it. */}
              <MoneyRow
                label="Delivery fees on your orders"
                value={fmtUGX(totals.deliveryFee + (totals.deliveryFundedByLusuku || 0))}
              />
              {totals.deliveryFundedByLusuku > 0 && (
                <MoneyRow
                  label="of which funded by Lusuku (customer delivery offers)"
                  value={fmtUGX(totals.deliveryFundedByLusuku)}
                />
              )}
              <MoneyRow label="Paid to riders" value={fmtUGX(totals.riderEarnings)} />
              <MoneyRow label="Your share" value={fmtUGX(totals.partnerEarnings)} />
              <MoneyRow
                label="Retained by Lusuku"
                value={fmtUGX(
                  Math.max(
                    0,
                    totals.deliveryFee +
                      (totals.deliveryFundedByLusuku || 0) -
                      totals.riderEarnings -
                      totals.partnerEarnings
                  )
                )}
              />
              <div style={{ ...S.muted, marginTop: 10 }}>
                Riders are paid first out of every fee. Your share and Lusuku's are what remains
                once the trip is covered.
              </div>
            </div>
            <div style={S.panel}>
              <SectionTitle first>By vehicle</SectionTitle>
              <ShareList rows={vehicleRows} emptyText="No deliveries in this window yet." />
            </div>
          </div>

          <SectionTitle>Riders</SectionTitle>
          <div style={S.scroll}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Rider</th>
                  <th style={S.th}>Vehicle</th>
                  <th style={S.th}>Pay model</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Deliveries</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Distance</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Earned</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Per delivery</th>
                </tr>
              </thead>
              <tbody>
                {(data.riders || []).length === 0 ? (
                  <tr>
                    <td style={S.td} colSpan={7}>
                      <span style={S.muted}>No completed deliveries in this window yet.</span>
                    </td>
                  </tr>
                ) : (
                  data.riders.map((r) => (
                    <tr key={r.riderId}>
                      <td style={{ ...S.td, fontWeight: 600, color: C.body }}>{r.name}</td>
                      <td style={S.td}>
                        <span style={S.pill}>
                          {VEHICLE_LABEL[r.carrierType] || r.carrierType || "Motorcycle"}
                        </span>
                      </td>
                      <td style={S.td}>{PAYOUT_LABEL[r.payoutModel] || r.payoutModel}</td>
                      <td style={S.tdNum}>{r.deliveries}</td>
                      <td style={S.tdNum}>{Math.round(r.distanceKm)} km</td>
                      <td style={{ ...S.tdNum, fontWeight: 700, color: C.ink }}>
                        {fmtUGX(r.riderEarnings)}
                      </td>
                      <td style={S.tdNum}>{fmtUGX(r.averagePerDelivery)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={S.note}>
            <strong>Reading the rider table.</strong> A salaried rider's figure is a running
            allowance for fuel and maintenance, not a share of the fee, so it is deliberately much
            smaller than a per-delivery rider's on the same trip. Compare salaried riders with each
            other, and per-delivery riders with each other.
          </div>
        </>
      )}
    </div>
  );
}
